import classNames from "classnames";
import {
  JSXElementConstructor,
  memo,
  ReactNode,
  useCallback,
  useId,
  useMemo,
  useState,
} from "react";
import { DescriptionText, ErrorText, LabelText } from "../common/descriptors";
import { Flex } from "../flex";
import { Text } from "../text";
import * as redoTextInputCss from "./base-redo-input.module.css";
import {
  RedoInputIdContext,
  RedoInputSize,
  RedoInputState,
  sizeStyles,
  sizeToDescriptorTextProps,
  sizeToInputTextProps,
  sizeToPx,
} from "./base-redo-text-input";

export type RedoInputAdornment =
  | { type: "text"; value: string }
  | { Icon: JSXElementConstructor<any>; type: "icon" };

export interface BaseRedoInputContainerProps {
  size?: RedoInputSize;
  state?: RedoInputState;
  label?: string;
  description?: string;
  errorText?: string;
  className?: string;
  children: ReactNode | ReactNode[];
  /**
   * Explicit id for the wrapped input. When omitted, a stable id is generated
   * so the `<label>` and input are still connected.
   */
  htmlFor?: string;
  /** Kebab-case component name prefixed onto the generated input id. */
  idPrefix?: string;
  dangerousStyleThatShouldOnlyBeUsedForMerchantBranding?: React.CSSProperties;
  fullWidth?: boolean;
  dynamicHeight?: boolean;
  uiSelector?: string;
  "data-arbiter"?: string;
  /** `data-target` on the bordered element, for merchant-authored CSS. */
  dataTarget?: string;

  leadingAdornment?: RedoInputAdornment;
  trailingAdornment?: RedoInputAdornment;
}

export const BaseRedoInputContainer = memo(function BaseRedoInputContainer({
  size = "sm",
  state = "default",
  label,
  description,
  errorText,
  className,
  htmlFor,
  idPrefix,
  dangerousStyleThatShouldOnlyBeUsedForMerchantBranding,
  children,
  fullWidth,
  dynamicHeight,
  uiSelector,
  dataTarget,
  leadingAdornment,
  trailingAdornment,
  "data-arbiter": dataArbiter,
}: BaseRedoInputContainerProps) {
  const generatedId = useId();
  const inputId =
    htmlFor ?? (idPrefix ? `${idPrefix}-${generatedId}` : generatedId);
  const descriptionId = description ? `${inputId}-description` : undefined;
  const errorMessageId = errorText ? `${inputId}-error` : undefined;
  // Connect the visible label only once a wrapped input registers, so a
  // container around a non-input (e.g. a color swatch) has no dangling htmlFor.
  const [inputRegistered, setInputRegistered] = useState(false);
  const register = useCallback(() => setInputRegistered(true), []);
  const idContextValue = useMemo(
    () => ({ descriptionId, errorMessageId, id: inputId, register }),
    [descriptionId, errorMessageId, inputId, register],
  );
  const connectLabel = inputRegistered || htmlFor !== undefined;
  const descriptorFontSize = sizeToDescriptorTextProps[size];

  const error = state === "error";
  const disabled = state === "disabled";
  const readonly = state === "readonly";
  const leadingAdornmentContent = renderRedoInputAdornment(
    leadingAdornment,
    size,
  );
  const trailingAdornmentContent = renderRedoInputAdornment(
    trailingAdornment,
    size,
  );

  return (
    <Flex
      data-arbiter={dataArbiter}
      data-ui={uiSelector}
      dir="column"
      gap="sm"
      minW="none"
      w={fullWidth ? "full" : undefined}
    >
      <LabelText
        htmlFor={connectLabel ? inputId : undefined}
        label={label}
        size={descriptorFontSize}
      />
      <Flex align="center" gap="none" p="none">
        {leadingAdornmentContent && (
          <Flex
            align="center"
            alignSelf="stretch"
            bgColor="disabled"
            className={classNames(redoTextInputCss.prefix, sizeStyles[size])}
            pl="md"
            pr="sm"
            py="xs"
            shrink={0}
            textColor="tertiary"
          >
            {leadingAdornmentContent}
          </Flex>
        )}
        <Flex
          align="center"
          bgColor="primary"
          className={classNames(
            disabled && redoTextInputCss.disabled,
            redoTextInputCss.inputWrapper,
            error && redoTextInputCss.error,
            !dynamicHeight && sizeStyles[size],
            readonly && redoTextInputCss.readonly,
            leadingAdornmentContent && redoTextInputCss.hasPrefix,
            trailingAdornmentContent && redoTextInputCss.hasSuffix,
            className,
          )}
          data-portal-selector="redo-input"
          data-target={dataTarget}
          gap="none"
          grow={1}
          minW="none"
          px={readonly ? undefined : sizeToPx[size]}
          style={{
            backgroundColor: readonly ? "transparent" : undefined,
            ...dangerousStyleThatShouldOnlyBeUsedForMerchantBranding,
          }}
          textColor="primary"
        >
          <RedoInputIdContext.Provider value={idContextValue}>
            {children}
          </RedoInputIdContext.Provider>
        </Flex>
        {trailingAdornmentContent && (
          <Flex
            align="center"
            alignSelf="stretch"
            bgColor="disabled"
            className={classNames(redoTextInputCss.suffix, sizeStyles[size])}
            pl="sm"
            pr="md"
            py="xs"
            shrink={0}
            textColor="tertiary"
          >
            {trailingAdornmentContent}
          </Flex>
        )}
      </Flex>
      <DescriptionText
        description={description}
        error={error}
        id={descriptionId}
        size={descriptorFontSize}
      />
      <ErrorText
        id={errorMessageId}
        size={descriptorFontSize}
        text={errorText}
      />
    </Flex>
  );
});

export function getTextAdornment(
  value: string | undefined,
): RedoInputAdornment | undefined {
  return value ? { type: "text", value } : undefined;
}

export function renderRedoInputAdornment(
  adornment: RedoInputAdornment | undefined,
  size: RedoInputSize,
) {
  if (!adornment) {
    return undefined;
  }

  if (adornment.type === "icon") {
    const Icon = adornment.Icon;
    return <Icon />;
  }

  return (
    <Text fontSize={sizeToInputTextProps[size].fontSize}>
      {adornment.value}
    </Text>
  );
}
